import { useEffect, useState, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { TrendingUp, DollarSign, Clock, AlertTriangle, RefreshCw, Boxes } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RevenueDashboard({ dataTestId = 'revenue-dashboard' }) {
  const [data, setData] = useState(null);
  const [bulkStats, setBulkStats] = useState({ scheduled: 0, completed: 0, totalFee: 0 });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [billRes, bulkRes, propsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/billing/dashboard`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/ops/bulk-pickup`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/properties`, { credentials: 'include' }),
      ]);
      if (billRes.ok) setData(await billRes.json());

      // Compute bulk-pickup revenue: sum of property bulk fee × completed/scheduled count
      if (bulkRes.ok && propsRes.ok) {
        const requests = await bulkRes.json();
        const props = await propsRes.json();
        const feeByProp = {};
        props.forEach((p) => { feeByProp[p.property_id] = parseFloat(p.bulk_collection?.additional_fee || 0); });

        let scheduled = 0, completed = 0, totalFee = 0;
        requests.forEach((r) => {
          if (r.status === 'scheduled') scheduled += 1;
          if (r.status === 'completed') {
            completed += 1;
            totalFee += feeByProp[r.property_id] || 0;
          }
        });
        setBulkStats({ scheduled, completed, totalFee });
      }
    } catch (_e) { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return <Card className="bg-zinc-900 border-zinc-700 p-8 text-center text-zinc-400" data-testid={dataTestId}>Loading…</Card>;
  }

  const t = data.totals;
  const buckets = data.aging_buckets;
  const totalAr = (buckets.current + buckets['1_30'] + buckets['31_60'] + buckets['60_plus']) || 1;

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      <Card className="bg-zinc-900 border-zinc-700 p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp className="h-6 w-6 text-[#C9A962]" />
          <div>
            <h3 className="text-white text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>REVENUE & A/R</h3>
            <p className="text-xs text-zinc-400">Billed vs collected, days-sales-outstanding, aging, and per-property collection rates.</p>
          </div>
        </div>
        <Button variant="outline" className="border-[#C9A962] text-[#C9A962]" onClick={fetchData} data-testid="revenue-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </Card>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="revenue-kpis">
        <Kpi label="Billed this month" value={`$${t.billed_this_month.toLocaleString()}`} accent="bg-[#C9A962] text-[#2D2D2D]" />
        <Kpi label="Total paid" value={`$${t.total_paid.toLocaleString()}`} accent="bg-green-600 text-white" />
        <Kpi label="Total outstanding" value={`$${t.total_outstanding.toLocaleString()}`} accent="bg-amber-600 text-white" />
        <Kpi label="DSO (avg days)" value={data.dso_days != null ? data.dso_days : '—'} accent="bg-zinc-800 text-white border border-[#C9A962]" />
      </div>

      {/* Bulk pickup revenue strip */}
      <Card className="bg-zinc-900 border border-[#C9A962]/40 p-4" data-testid="revenue-bulk-strip">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded bg-[#C9A962] text-black">
              <Boxes className="h-5 w-5" />
            </div>
            <div>
              <h4 className="text-white font-bold">Bulk pickup revenue</h4>
              <p className="text-xs text-zinc-400">Per-fee earnings from bulk collections (completed only).</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Mini label="Scheduled" value={bulkStats.scheduled} />
            <Mini label="Completed" value={bulkStats.completed} />
            <Mini label="Earned" value={`$${bulkStats.totalFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} accent />
          </div>
        </div>
      </Card>

      {/* Aging buckets */}
      <Card className="bg-zinc-900 border-zinc-700 p-5">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><Clock className="h-4 w-4 text-[#C9A962]" /> Aging</h4>
        <div className="space-y-2" data-testid="revenue-aging">
          {[
            { key: 'current', label: 'Current (not yet due)', color: '#16a34a' },
            { key: '1_30', label: '1–30 days late', color: '#eab308' },
            { key: '31_60', label: '31–60 days late', color: '#f97316' },
            { key: '60_plus', label: '60+ days late', color: '#dc2626' },
          ].map(({ key, label, color }) => {
            const v = buckets[key] || 0;
            const pct = (v / totalAr) * 100;
            return (
              <div key={key} className="flex items-center gap-3" data-testid={`aging-${key}`}>
                <div className="w-40 text-xs text-zinc-300">{label}</div>
                <div className="flex-1 h-3 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                </div>
                <div className="w-32 text-right text-sm text-white font-mono">${v.toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Collection rates per property */}
      <Card className="bg-zinc-900 border-zinc-700 p-5">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><DollarSign className="h-4 w-4 text-[#C9A962]" /> Collection Rate per Property</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="revenue-collections">
            <thead>
              <tr className="text-left text-zinc-400 border-b border-white/10">
                <th className="py-2 pr-3">Property</th>
                <th className="py-2 pr-3">Billed</th>
                <th className="py-2 pr-3">Paid</th>
                <th className="py-2 pr-3">Outstanding</th>
                <th className="py-2 pr-3">Invoices</th>
                <th className="py-2 pr-3">Rate</th>
              </tr>
            </thead>
            <tbody>
              {data.collection_rates.map((p) => (
                <tr key={p.property_id} className="border-b border-white/5">
                  <td className="py-2 pr-3 text-white">{p.property_name || p.property_id}</td>
                  <td className="py-2 pr-3 text-white">${p.billed.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-green-400">${p.paid.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-amber-400">${p.outstanding.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-zinc-300">{p.invoice_count}</td>
                  <td className="py-2 pr-3">
                    <Badge className={p.collection_rate_pct >= 80 ? 'bg-green-700' : p.collection_rate_pct >= 50 ? 'bg-amber-700' : 'bg-red-700'}>
                      {p.collection_rate_pct}%
                    </Badge>
                  </td>
                </tr>
              ))}
              {data.collection_rates.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-zinc-500 text-center">No data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Top overdue */}
      <Card className="bg-zinc-900 border-zinc-700 p-5">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-400" /> Top Overdue Invoices</h4>
        {data.top_overdue.length === 0 ? (
          <p className="text-zinc-500 text-sm" data-testid="revenue-overdue-empty">No overdue invoices. 💪</p>
        ) : (
          <div className="space-y-2" data-testid="revenue-overdue">
            {data.top_overdue.map((o) => (
              <div key={o.invoice_id} className="rounded-lg p-3 bg-[#222] border border-red-500/30 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-[#C9A962] font-mono text-xs">{o.invoice_number}</p>
                  <p className="text-white text-sm">{o.property_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-red-400 font-bold">${o.balance.toLocaleString()}</p>
                  <p className="text-[11px] text-zinc-500">{o.days_late} days late · due {o.due_date}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, accent = 'bg-zinc-800 text-white' }) {
  return (
    <div className={`rounded-lg p-4 ${accent}`}>
      <p className="text-3xl font-black leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{value}</p>
      <p className="text-[11px] uppercase tracking-widest opacity-80 mt-2 font-semibold">{label}</p>
    </div>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div className={`px-3 py-2 rounded-md text-right ${accent ? 'bg-[#C9A962] text-black' : 'bg-zinc-800 text-white'}`}>
      <div className="text-lg font-bold leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest opacity-80 mt-1">{label}</div>
    </div>
  );
}

